/**
 * @file form-template/applicability.ts
 * @description 表單（form_templates）適用範圍匹配純函式——前後端共用（2026-09-25 回報序37/38）。
 *
 * 表單有三個「留空 = 不限」的適用範圍欄位：
 *  - applicableSchemes     適用方案（TOP/TAP/ECO）
 *  - applicableTaskTypes   適用驗證類型（序37 起改用後台「驗證類型」字典名稱，如「初次申請」「追蹤查驗1」；
 *                          舊資料可能仍是 initial/renewal/surveillance/special 任務類型代碼，需相容）
 *  - applicableCategories  適用驗證類別（標籤，如「作物」「加工」「分裝」「流通」「蜂蜜」「通用」）
 *
 * 舊實作的兩個 bug（序38）：
 *  1. 前端多選框清空後送出的是 `[]` 而非 null，後端/前端卻只把 null 當「不限」，
 *     導致「留空 = 不限」的表單在稽核員工作台查核表單清單中消失。
 *     → 本模組一律把 null / [] / 只含空字串 視為「不限」。
 *  2. applicableCategories 從未參與過濾，勾選「蜂蜜/加工/分裝/流通」的表單仍出現在作物案件中。
 *     → 以 {@link canonicalCategoryTags} 將「表單標籤」與「案件驗證類別字典值」
 *        （如「有機作物」「農糧產品分裝」）都正規化為同一組標籤再取交集。
 */
import { splitVerificationTypes } from '../schemas/cases';
/** 舊任務類型代碼（applicableTaskTypes 歷史值） */
const LEGACY_TASK_TYPE_CODES = [
    'initial',
    'renewal',
    'surveillance',
    'special',
];
/**
 * 驗證類型名稱前綴 → 舊任務類型代碼（與 apps/api audit-task-bootstrap 的 inferTaskType 同口徑）。
 * 用於：舊資料 applicableTaskTypes=['initial'] 時，判斷案件的「初次申請」是否命中。
 */
const VERIFICATION_TYPE_PREFIX_TO_LEGACY = [
    ['初次申請', 'initial'],
    ['繼續驗證', 'renewal'],
    ['展延查驗', 'renewal'],
    ['追蹤查驗', 'surveillance'],
];
/** 代表「所有類別皆適用」的標籤 */
const UNIVERSAL_CATEGORY_TAGS = new Set(['通用', '全部', '不限']);
/**
 * 判斷陣列型適用範圍是否為「不限」：null / undefined / 空陣列 / 全為空字串。
 * @param list 適用範圍陣列
 */
export function isUnrestrictedList(list) {
    if (!list || list.length === 0)
        return true;
    return list.every((v) => !v || !v.trim());
}
/**
 * 把「留空」正規化為 null（寫庫前使用，避免 `[]` 與 null 語義分裂）。
 * 同時去除前後空白、空字串與重複值。
 * @param list 前端送來的陣列
 * @returns 非空陣列或 null
 */
export function normalizeApplicableList(list) {
    if (!list)
        return null;
    const cleaned = [...new Set(list.map((v) => (v ?? '').trim()).filter(Boolean))];
    return cleaned.length > 0 ? cleaned : null;
}
/**
 * 正規化驗證類型名稱：全形括號 → 半形、移除空白，讓「初次申請（轉證）」與「初次申請(轉證)」視為相同。
 * @param name 驗證類型名稱
 */
export function normalizeVerificationTypeName(name) {
    return name.replace(/（/g, '(').replace(/）/g, ')').replace(/\s+/g, '').trim();
}
/**
 * 將單一驗證類型名稱歸類為舊任務類型代碼（未命中前綴歸 'special'）。
 * @param name 驗證類型名稱（或舊資料直接存的代碼）
 */
export function legacyTaskTypeOf(name) {
    const trimmed = name.trim();
    if (LEGACY_TASK_TYPE_CODES.includes(trimmed)) {
        return trimmed;
    }
    const hit = VERIFICATION_TYPE_PREFIX_TO_LEGACY.find(([prefix]) => trimmed.startsWith(prefix));
    return hit ? hit[1] : 'special';
}
/**
 * 判斷值是否為舊任務類型代碼（initial/renewal/surveillance/special）。
 * @param value 適用驗證類型陣列中的單值
 */
export function isLegacyTaskTypeCode(value) {
    return LEGACY_TASK_TYPE_CODES.includes(value);
}
/**
 * 將舊任務類型代碼展開為驗證類型字典名稱（編輯表單時把舊資料轉成字典值顯示）。
 * 非舊代碼的值原樣保留；展開結果保序去重。
 *
 * @param values 表單現存的 applicableTaskTypes
 * @param dictionaryNames 驗證類型字典名稱（啟用中）
 * @returns 只含字典名稱（或無法對應的原值）的陣列
 */
export function expandLegacyTaskTypes(values, dictionaryNames) {
    if (!values)
        return [];
    const out = [];
    for (const v of values) {
        if (isLegacyTaskTypeCode(v)) {
            for (const name of dictionaryNames) {
                if (legacyTaskTypeOf(name) === v)
                    out.push(name);
            }
        }
        else {
            out.push(v);
        }
    }
    return [...new Set(out)];
}
/**
 * 判斷表單的「適用驗證類型」是否命中案件驗證類型（案件可複選，以「、」拼接）。
 *
 * - 表單留空（null/[]）→ 不限，命中。
 * - 案件驗證類型未知（空）→ 不以此過濾，命中。
 * - 表單值為字典名稱：與案件任一驗證類型「正規化後全等」即命中。
 * - 表單值為舊代碼（initial…）：案件任一驗證類型歸類後等於該代碼即命中。
 *
 * @param templateTypes 表單 applicableTaskTypes
 * @param caseVerificationType 案件 verification_type（可為多選拼接字串）
 */
export function matchesVerificationType(templateTypes, caseVerificationType) {
    if (isUnrestrictedList(templateTypes))
        return true;
    const parts = splitVerificationTypes(caseVerificationType);
    if (parts.length === 0)
        return true;
    const caseNames = new Set(parts.map(normalizeVerificationTypeName));
    const caseLegacy = new Set(parts.map(legacyTaskTypeOf));
    return (templateTypes ?? []).some((raw) => {
        const v = raw.trim();
        if (!v)
            return false;
        if (isLegacyTaskTypeCode(v))
            return caseLegacy.has(v);
        return caseNames.has(normalizeVerificationTypeName(v));
    });
}
/**
 * 將驗證類別字串（表單標籤或案件類別字典值）正規化為一組標準標籤。
 *
 * 規則（含多個關鍵字時全部收集）：
 *  - 含「蜂蜜」→ 蜂蜜；含「加工」→ 加工；含「分裝」→ 分裝；含「流通」→ 流通
 *  - 含「集團」→ 集團；含「TGAP」→ TGAP；含「履歷轉換」→ 履歷轉換
 *  - 含「作物/種植/農場」→ 作物
 *  - 以上皆未命中但含「農糧產品 / 農產品」（TAP 的生產類別）→ 作物
 *  - 仍未命中 → 以去空白原字串作唯一標籤（自由文字仍可精確匹配）
 *
 * 例：「分裝流通」→ [分裝, 流通]；「有機作物」→ [作物]；「農糧產品分裝」→ [分裝]；「農糧加工品」→ [加工]。
 *
 * @param value 類別字串
 */
export function canonicalCategoryTags(value) {
    const s = value.replace(/\s+/g, '');
    if (!s)
        return [];
    const tags = [];
    if (s.includes('蜂蜜'))
        tags.push('蜂蜜');
    if (s.includes('加工'))
        tags.push('加工');
    if (s.includes('分裝'))
        tags.push('分裝');
    if (s.includes('流通'))
        tags.push('流通');
    if (s.includes('集團'))
        tags.push('集團');
    if (s.toUpperCase().includes('TGAP'))
        tags.push('TGAP');
    if (s.includes('履歷轉換'))
        tags.push('履歷轉換');
    if (s.includes('作物') || s.includes('種植') || s.includes('農場'))
        tags.push('作物');
    if (tags.length === 0 && (s.includes('農糧產品') || s.includes('農產品')))
        tags.push('作物');
    return tags.length > 0 ? tags : [s];
}
/**
 * 判斷表單的「適用驗證類別」是否命中案件驗證類別。
 *
 * - 表單留空或含「通用」→ 不限，命中。
 * - 案件未填驗證類別 → 無法判斷，不以此過濾（命中），避免舊案件查無表單。
 * - 其餘：雙方正規化標籤取交集，非空即命中。
 *
 * @param templateCategories 表單 applicableCategories
 * @param caseCategories 案件 verification_categories（jsonb 陣列）
 */
export function matchesVerificationCategories(templateCategories, caseCategories) {
    if (isUnrestrictedList(templateCategories))
        return true;
    const tplTags = new Set((templateCategories ?? []).flatMap((c) => UNIVERSAL_CATEGORY_TAGS.has(c.trim()) ? ['*'] : canonicalCategoryTags(c)));
    if (tplTags.has('*'))
        return true;
    const caseList = (caseCategories ?? []).filter((c) => typeof c === 'string' && c.trim());
    if (caseList.length === 0)
        return true;
    return caseList.some((c) => canonicalCategoryTags(c).some((t) => tplTags.has(t)));
}
/**
 * 判斷表單的「適用方案」是否命中案件方案。
 * applicableSchemes 留空時回落單值 scheme（向後兼容）；兩者皆空 = 通用。
 *
 * @param tpl 表單適用方案欄位
 * @param caseScheme 案件方案（未知時不過濾）
 */
export function matchesScheme(tpl, caseScheme) {
    if (!caseScheme)
        return true;
    const schemes = !isUnrestrictedList(tpl.applicableSchemes)
        ? tpl.applicableSchemes
        : tpl.scheme
            ? [tpl.scheme]
            : null;
    if (!schemes)
        return true;
    return schemes.includes(caseScheme);
}
/**
 * 綜合判斷表單是否適用於案件（方案 ∧ 驗證類型 ∧ 驗證類別）。
 * 稽核任務「查核表單」清單（前端 FormSelector）與派案自動建表（後端 bootstrap）共用。
 *
 * @param tpl 表單適用範圍
 * @param ctx 案件條件
 */
export function isFormTemplateApplicable(tpl, ctx) {
    return (matchesScheme(tpl, ctx.scheme) &&
        matchesVerificationType(tpl.applicableTaskTypes, ctx.verificationType) &&
        matchesVerificationCategories(tpl.applicableCategories, ctx.verificationCategories));
}
